/*
 * Назначение: страница «База знаний».
 * Функционал: вкладки «О компании», «Услуги», «FAQ», «Возражения», «Правила»
 * — CRUD структурированных knowledge_items соответствующего типа. Общие
 * настройки проекта (тон, цели, запреты, промпт) перенесены в раздел
 * «Настройки». Все данные — через /api/admin/knowledge.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Edit,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { knowledgeApi } from "../../lib/admin-api";
import { ApiError } from "../../lib/api";
import { useProject } from "../components/project/project-provider";
import type { KnowledgeDocument, KnowledgeItem, KnowledgeKind } from "../../lib/types";
import { formatDateTime } from "../../lib/format";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";

// ============== Подвкладки CRUD ==============

const KIND_TO_LABEL: Record<KnowledgeKind, { title: string; subtitle: string }> = {
  company: { title: "О компании", subtitle: "Краткие факты, чем занимаемся, для кого" },
  service: { title: "Услуги", subtitle: "Список услуг с ценами и сроками" },
  faq: { title: "Частые вопросы", subtitle: "Пары «вопрос — ответ»" },
  objection: { title: "Возражения", subtitle: "Возражения и как на них отвечать" },
  rule: { title: "Правила", subtitle: "Правила и условия работы, рабочие часы" },
};

interface ItemFormState {
  title: string;
  content: string;
  extraText: string;
  is_active: boolean;
}

function blankForm(): ItemFormState {
  return { title: "", content: "", extraText: "", is_active: true };
}

function parseExtra(text: string): Record<string, unknown> {
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    throw new Error("ожидается объект JSON");
  } catch (err) {
    throw new Error(
      `Поле «Доп. данные» должно быть JSON-объектом: ${(err as Error).message}`,
    );
  }
}

function KnowledgeKindTab({ kind }: { kind: KnowledgeKind }) {
  const queryClient = useQueryClient();
  const { currentSlug } = useProject();
  const queryKey = ["kb", currentSlug, kind];

  const { data: items = [], isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => knowledgeApi.list(kind),
    enabled: !!currentSlug,
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<KnowledgeItem | null>(null);
  const [form, setForm] = useState<ItemFormState>(blankForm());
  const [error, setError] = useState<string | null>(null);

  const startCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setError(null);
    setOpen(true);
  };

  const startEdit = (item: KnowledgeItem) => {
    setEditing(item);
    setForm({
      title: item.title,
      content: item.content,
      extraText: Object.keys(item.extra || {}).length
        ? JSON.stringify(item.extra, null, 2)
        : "",
      is_active: item.is_active,
    });
    setError(null);
    setOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: (payload: {
      title: string;
      content: string;
      extra: Record<string, unknown>;
      is_active: boolean;
    }) =>
      knowledgeApi.create({
        kind,
        title: payload.title,
        content: payload.content,
        extra: payload.extra,
        is_active: payload.is_active,
      }),
    onSuccess: () => {
      toast.success("Элемент добавлен");
      queryClient.invalidateQueries({ queryKey });
      setOpen(false);
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Не удалось сохранить элемент");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        title: string;
        content: string;
        extra: Record<string, unknown>;
        is_active: boolean;
      };
    }) => knowledgeApi.update(id, payload),
    onSuccess: () => {
      toast.success("Изменения сохранены");
      queryClient.invalidateQueries({ queryKey });
      setOpen(false);
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Не удалось сохранить изменения");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => knowledgeApi.remove(id),
    onSuccess: () => {
      toast.success("Элемент удалён");
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => toast.error("Не удалось удалить элемент"),
  });

  const handleSubmit = () => {
    setError(null);
    const title = form.title.trim();
    const content = form.content.trim();
    if (!title) return setError("Название обязательно");
    if (!content) return setError("Содержимое обязательно");

    let extra: Record<string, unknown>;
    try {
      extra = parseExtra(form.extraText);
    } catch (e) {
      return setError((e as Error).message);
    }

    if (editing) {
      updateMutation.mutate({
        id: editing.id,
        payload: { title, content, extra, is_active: form.is_active },
      });
    } else {
      createMutation.mutate({ title, content, extra, is_active: form.is_active });
    }
  };

  const meta = KIND_TO_LABEL[kind];
  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{meta.title}</CardTitle>
          <CardDescription>{meta.subtitle}</CardDescription>
        </div>
        <Button onClick={startCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="text-muted-foreground text-sm flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загружаем…
          </div>
        )}

        {isError && (
          <div className="text-sm text-destructive flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Не удалось загрузить элементы
          </div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <div className="text-sm text-muted-foreground py-8 text-center">
            Пока пусто. Нажми «Добавить», чтобы создать первый элемент.
          </div>
        )}

        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{item.title}</h4>
                      {!item.is_active && (
                        <Badge variant="outline">Скрыт</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">
                      {item.content}
                    </p>
                    {Object.keys(item.extra || {}).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(item.extra).map(([k, v]) => (
                          <Badge key={k} variant="outline" className="text-xs">
                            {k}: {String(v)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(item)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        if (confirm(`Удалить «${item.title}»?`)) {
                          deleteMutation.mutate(item.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Редактировать: ${editing.title}` : `Новый элемент — ${meta.title}`}
            </DialogTitle>
            <DialogDescription>
              {kind === "faq"
                ? "В «Названии» — вопрос, в «Содержимом» — ответ."
                : kind === "objection"
                  ? "В «Названии» — возражение, в «Содержимом» — как отвечать."
                  : "Конкретно и кратко: ИИ возьмёт это в контекст ответа."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="kb-title">Название</Label>
              <Input
                id="kb-title"
                value={form.title}
                onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                disabled={isMutating}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kb-content">Содержимое</Label>
              <Textarea
                id="kb-content"
                rows={5}
                value={form.content}
                onChange={(e) => setForm((s) => ({ ...s, content: e.target.value }))}
                disabled={isMutating}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kb-extra">Доп. данные (JSON)</Label>
              <Textarea
                id="kb-extra"
                rows={3}
                placeholder='Например: {"price_from": 35000, "price_to": 70000}'
                value={form.extraText}
                onChange={(e) => setForm((s) => ({ ...s, extraText: e.target.value }))}
                disabled={isMutating}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="kb-active"
                checked={form.is_active}
                onCheckedChange={(checked) =>
                  setForm((s) => ({ ...s, is_active: Boolean(checked) }))
                }
                disabled={isMutating}
              />
              <Label htmlFor="kb-active">Показывать ИИ</Label>
            </div>
            {error && (
              <div className="text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={isMutating}>
              Отмена
            </Button>
            <Button onClick={handleSubmit} disabled={isMutating}>
              {isMutating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Сохранить" : "Добавить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DocumentsTab() {
  const queryClient = useQueryClient();
  const { currentSlug } = useProject();
  const queryKey = ["kb-docs", currentSlug];

  const { data: docs = [], isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => knowledgeApi.listDocuments(),
    enabled: !!currentSlug,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => knowledgeApi.uploadDocument(file),
    onSuccess: (doc) => {
      if (doc.status === "ready") {
        toast.success(`Документ «${doc.filename}» загружен и обработан`);
      } else {
        toast.error(`Документ загружен, но не обработан: ${doc.error_message || "ошибка"}`);
      }
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось загрузить");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, include }: { id: string; include: boolean }) =>
      knowledgeApi.updateDocument(id, include),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось обновить");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => knowledgeApi.removeDocument(id),
    onSuccess: () => {
      toast.success("Документ удалён");
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось удалить");
    },
  });

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    e.target.value = ""; // сброс, чтобы можно было загрузить тот же файл повторно
  };

  const STATUS_LABEL: Record<string, string> = {
    uploaded: "загружен",
    indexing: "обрабатывается",
    ready: "готов",
    error: "ошибка",
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Документы</CardTitle>
            <CardDescription>
              Загрузи прайсы, презентации, описания услуг (PDF, DOCX, TXT, MD,
              PPTX, XLSX, CSV, HTML). Текст из них автоматически попадает в
              знания ИИ. Лишний документ можно временно отключить, не удаляя.
            </CardDescription>
          </div>
          <div>
            <input
              type="file"
              id="kb-doc-upload"
              className="hidden"
              accept=".pdf,.docx,.doc,.txt,.md,.rtf,.pptx,.xlsx,.csv,.html,.htm"
              onChange={onPickFile}
            />
            <Button
              onClick={() => document.getElementById("kb-doc-upload")?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Загрузить документ
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Загружаем…
          </div>
        ) : isError ? (
          <div className="flex items-center text-destructive text-sm">
            <AlertCircle className="h-4 w-4 mr-2" />
            Не удалось загрузить список
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">
            Документов пока нет. Загрузи первый — например, прайс-лист в PDF.
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((doc: KnowledgeDocument) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 rounded-md border p-3"
              >
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.filename}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span>{(doc.size_bytes / 1024).toFixed(0)} КБ</span>
                    <span>·</span>
                    <span>{formatDateTime(doc.uploaded_at)}</span>
                    {doc.status === "ready" && (
                      <>
                        <span>·</span>
                        <span>{doc.chars.toLocaleString("ru")} символов</span>
                      </>
                    )}
                  </div>
                  {doc.status === "error" && doc.error_message && (
                    <p className="text-xs text-destructive mt-1">{doc.error_message}</p>
                  )}
                </div>
                <Badge
                  variant={
                    doc.status === "ready"
                      ? "outline"
                      : doc.status === "error"
                        ? "destructive"
                        : "secondary"
                  }
                  className="shrink-0"
                >
                  {STATUS_LABEL[doc.status] ?? doc.status}
                </Badge>
                {doc.status === "ready" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title={doc.include_in_context ? "Используется в ИИ — выключить" : "Включить в ИИ"}
                    onClick={() =>
                      toggleMutation.mutate({ id: doc.id, include: !doc.include_in_context })
                    }
                  >
                    {doc.include_in_context ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  title="Удалить"
                  onClick={() => removeMutation.mutate(doc.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const TAB_BY_PATH: Record<string, string> = {
  services: "services",
  faq: "faq",
  objections: "objections",
  rules: "rules",
  company: "company",
  documents: "documents",
};

export function KnowledgeBasePage() {
  const params = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const tab = (params.tab && TAB_BY_PATH[params.tab]) || "services";

  const setTab = (next: string) => {
    if (next === "services") {
      navigate("/knowledge-base", { replace: false });
    } else {
      navigate(`/knowledge-base/${next}`, { replace: false });
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-semibold">База знаний</h2>
        <p className="text-sm text-muted-foreground">
          Содержимое, которым пользуется ИИ-продавец. Изменения применяются мгновенно.
          Общие настройки тонa, целей и запретов — в разделе «Настройки».
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full h-auto grid-cols-3 sm:grid-cols-6">
          <TabsTrigger value="company">О компании</TabsTrigger>
          <TabsTrigger value="services">Услуги</TabsTrigger>
          <TabsTrigger value="faq">FAQ</TabsTrigger>
          <TabsTrigger value="objections">Возражения</TabsTrigger>
          <TabsTrigger value="rules">Правила</TabsTrigger>
          <TabsTrigger value="documents">Документы</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="space-y-6">
          <KnowledgeKindTab kind="company" />
        </TabsContent>
        <TabsContent value="services" className="space-y-6">
          <KnowledgeKindTab kind="service" />
        </TabsContent>
        <TabsContent value="faq" className="space-y-6">
          <KnowledgeKindTab kind="faq" />
        </TabsContent>
        <TabsContent value="objections" className="space-y-6">
          <KnowledgeKindTab kind="objection" />
        </TabsContent>
        <TabsContent value="rules" className="space-y-6">
          <KnowledgeKindTab kind="rule" />
        </TabsContent>
        <TabsContent value="documents" className="space-y-6">
          <DocumentsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
